/**
 * Function to load YouTube IFrame API.
 *
 * @param {Function} callback - Function to call once the YouTube IFrame API is loaded.
 */
export function loadYouTubeAPI(callback) {
  if (!window.YT) {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.onload = callback;
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = callback;
  } else {
    callback();
  }
}

/**
 * Set up event listener for YouTube state changes.
 *
 * @param {HTMLElement} iframe - The iframe element for the YouTube video.
 * @param {HTMLElement} videoWrapper - The video wrapper element.
 */
export function setupYouTubeStateChangeListener(iframe, videoWrapper) {
  const player = new YT.Player(iframe.id, {
    events: {
      'onReady': (event) => {
        if (videoWrapper.getAttribute('data-autoplay') === 'true') {
          event.target.playVideo();
        }
      },
      'onStateChange': (event) => {
        switch (event.data) {
          case YT.PlayerState.PLAYING:
            videoWrapper.setAttribute('data-video-state', 'playing');
            break;
          case YT.PlayerState.PAUSED:
            videoWrapper.setAttribute('data-video-state', 'paused');
            break;
          case YT.PlayerState.ENDED:
            videoWrapper.setAttribute('data-video-state', 'ended');
            break;
          default:
            videoWrapper.setAttribute('data-video-state', 'loading');
        }
      }
    }
  });
}

/**
 * Create and append a YouTube iframe to the specified video wrapper.
 *
 * @param {HTMLElement} videoWrapper   The wrapper element to load the video into.
 * @param {string} youtubeVideoUrl     The YouTube video URL.
 * @param {Object} options             The options for the video.
 * @param {boolean} options.autoplay   Whether the video should autoplay.
 * @param {boolean} options.muted      Whether the video should be muted.
 * @param {boolean} options.loop       Whether the video should loop.
 * @param {boolean} options.controls   Whether the video should show controls.
 */
export function createYouTubeIframe(videoWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls }) {
  const params = new URLSearchParams();
  params.set('rel', '0');
  if (autoplay) params.set('autoplay', '1');
  if (muted) params.set('mute', '1');
  if (loop) {
    params.set('loop', '1');
    const videoId = youtubeVideoUrl.split('/').pop();
    params.set('playlist', videoId);
  }
  if (!controls) params.set('controls', '0');
  params.set('enablejsapi', '1');
  params.set('origin', window.location.origin);

  const embedUrl = `${youtubeVideoUrl}?${params.toString()}`;

  const iframe = document.createElement('iframe');
  iframe.src = embedUrl;
  iframe.width = '100%';
  iframe.height = '100%';
  iframe.frameBorder = '0';
  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
  iframe.referrerPolicy = 'strict-origin-when-cross-origin';
  iframe.allowFullscreen = true;
  iframe.id = `youtube-player-${Math.random().toString(36).slice(2, 11)}`;

  const hasImageContainer = videoWrapper.querySelector('.pr-video__poster-image-container');
  const contentWrapper = videoWrapper.querySelector('.pr-video__content');
  const iframeWrapper = videoWrapper.querySelector('.pr-video__wrapper');

  if (!hasImageContainer && contentWrapper && iframeWrapper) {
    iframeWrapper.innerHTML = '';
    iframeWrapper.appendChild(iframe);
  } else {
    videoWrapper.innerHTML = '';
    videoWrapper.appendChild(iframe);
  }
}

/**
 * Preload a YouTube iframe in the specified video wrapper.
 *
 * @param {HTMLElement} videoWrapper   The wrapper element to load the video into.
 * @param {string} youtubeVideoUrl     The YouTube video URL.
 * @param {Object} options             The options for the video.
 * @param {boolean} options.autoplay   Whether the video should autoplay.
 * @param {boolean} options.muted      Whether the video should be muted.
 * @param {boolean} options.loop       Whether the video should loop.
 * @param {boolean} options.controls   Whether the video should show controls.
 */
export function preloadYouTubeIframe(videoWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls }) {
  if (!(videoWrapper instanceof HTMLElement)) {
    console.error('videoWrapper is not a valid HTML element', videoWrapper);
    return;
  }

  if (!youtubeVideoUrl) {
    console.error('Invalid YouTube video URL:', youtubeVideoUrl);
    return;
  }

  loadYouTubeAPI(() => {
    createYouTubeIframe(videoWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls });
    const iframe = videoWrapper.querySelector('iframe');
    if (iframe) {
      iframe.style.display = 'none';
    }
  });
}

/**
 * Load a YouTube video into the specified video wrapper.
 *
 * @param {HTMLElement} videoWrapper   The wrapper element to load the video into.
 * @param {string} youtubeVideoUrl     The YouTube video URL.
 * @param {Object} options             The options for the video.
 * @param {boolean} options.autoplay   Whether the video should autoplay.
 * @param {boolean} options.muted      Whether the video should be muted.
 * @param {boolean} options.loop       Whether the video should loop.
 * @param {boolean} options.controls   Whether the video should show controls.
 */
export function loadYouTubeVideo(videoWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls }) {
  if (!(videoWrapper instanceof HTMLElement)) {
    console.error('videoWrapper is not a valid HTML element', videoWrapper);
    return;
  }

  if (!youtubeVideoUrl) {
    console.error('Invalid YouTube video URL:', youtubeVideoUrl);
    return;
  }

  loadYouTubeAPI(() => {
    createYouTubeIframe(videoWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls });
    const iframe = videoWrapper.querySelector('iframe');
    if (iframe) {
      iframe.style.display = 'block';
    }
  });
}
